import type { PartnerAccount } from "@hakili/contracts";
import { t } from "@hakili/i18n";
import { GuardedButton, Modal } from "@hakili/ui";
import { Mail, Plus, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

import { ClientAccounts, PersonErasureDialog, type ClientAccountDoors } from "./client-accounts.js";
import type { Partner } from "./dealerships.js";
import { fill, StatusChip } from "./partner-cards.js";
import { PartnerAddModal } from "./partner-tabs.js";
import { roleWord } from "../shell/roles.js";

/**
 * `round CN-B`, `CN-B.3`: **« Comptes et accès », WF-CON-40, the
 * administration alone, to the drawing (`hakili-console.jsx`,
 * `ComptesScreen`).**
 *
 * Two tables from identity. The ORGANISATIONS from `GET /organisations` ,
 * type, member count, status , which is where an organisation still in
 * verification appears with zero accounts (WF-CON-40's acceptance criterion:
 * the consolidated accounts door joins users and cannot list it). The
 * ACCOUNTS from `GET /accounts` , person, organisation, role, how the account
 * was created (« par Hakili » for `created_by = hakili`, « invitée par le
 * chef » for a colleague the partner's own responsible invited), status.
 *
 * « Ouvrir une organisation » opens the drawing's modal over the veil , the
 * type, the name, the first responsible by email, the two offline checks the
 * door requires , by `POST /organisations` then `POST /organisations/{id}/first-admin`;
 * the drawing's « Domaine de messagerie » has no door and is said in words.
 * Deactivating a partner's account is, in the ordinary way, `identity`'s door
 * for that partner's own responsible (I16 and 6.1.8's scope). Since the founder's ruling of 16 October 2026 the
 * administration holds an override too (`POST /accounts/{userId}/deactivate`, reason required): « Désactiver » is
 * enabled for the seat that holds `administrerComptesPartenaires`, and greyed with the ordinary path's reason for
 * any other. A relation view is refused
 * this screen by the rail and by the door.
 *
 * Round 8, `M2`: below the two tables, « Comptes clients » (`client-accounts.tsx`), the administration's search
 * and its deactivate / reactivate gesture on a CLIENT's account, which is not a partner's and is the Hakili
 * administration's to cut (WF-CON-40 extended).
 */

/** One partner account, as `GET /api/v1/accounts` sends it: `@hakili/contracts`' {@link PartnerAccount} (`GENERIC-DOOR-SWEEP`, Arc 1). */
export type AccountRowView = PartnerAccount;

export interface AccountsDoors extends ClientAccountDoors {
  readonly readAccounts: () => Promise<readonly AccountRowView[]>;
  /** Partner accounts, `M1`: the administration's override. The reason is required and kept in the audit trail. */
  readonly deactivateAccount: (userId: string, reason: string) => Promise<void>;
  /** Partner accounts, `M2`: the irreversible one. The name typed back travels with the reason, and the door checks both. */
  readonly erasePartnerUser: (
    userId: string,
    input: { reason: string; confirmFullName: string },
  ) => Promise<void>;
  readonly openOrganisation: (input: {
    name: string;
    type: string;
    responsibleName: string;
    responsibleEmail: string;
  }) => Promise<void>;
}

export interface AccountsProps extends AccountsDoors {
  /** Whether the viewer holds `effacerComptesClients` (06 §6.1.5 row 33): the erasure is greyed without it. */
  readonly mayErase: boolean;
  /** `administrerComptesPartenaires` (06 §6.1.5 row 32): the override. Without it the gesture is shown greyed, with the reason. */
  readonly mayDeactivate: boolean;
  /** `effacerComptesPartenaires` (06 §6.1.5 row 34): the erasure of a partner's user, narrower than the deactivation's right. */
  readonly mayErasePartner: boolean;
  readonly partners: readonly Partner[] | null;
  readonly onOpened: () => void;
}

const TYPE_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["banque", "console.partenaires.typeBanque"],
  ["concessionnaire", "console.partenaires.typeConcessionnaire"],
  ["cabinet", "console.partenaires.typeCabinet"],
  ["assureur", "console.partenaires.typeAssureur"],
]);
const STATUS_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["actif", "console.comptes.statutActif"],
  ["invitationEnAttente", "console.comptes.statutInvitation"],
  ["desactive", "console.comptes.statutDesactive"],
]);
const STATUS_CHIP = new Map<string, string>([
  ["actif", "bg-emerald-50 text-emerald-700"],
  ["invitationEnAttente", "bg-amber-50 text-amber-700"],
  ["desactive", "bg-gray-100 text-gray-600"],
]);
const OPENABLE_TYPES = ["banque", "concessionnaire", "cabinet"] as const;

const nameFromEmail = (address: string): string =>
  (address.split("@")[0] ?? "")
    .split(/[._-]+/u)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const word = (table: Map<string, Parameters<typeof t>[0]>, code: string): string => {
  const key = table.get(code);
  return key === undefined ? code : t(key);
};

export function Accounts(props: AccountsProps): React.JSX.Element {
  const [accounts, setAccounts] = useState<readonly AccountRowView[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<string>("banque");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const [closing, setClosing] = useState<AccountRowView | null>(null);
  const [closingReason, setClosingReason] = useState("");
  const [closingRefusal, setClosingRefusal] = useState<string | null>(null);
  const [closed, setClosed] = useState<string | null>(null);
  const [erasing, setErasing] = useState<AccountRowView | null>(null);
  const { readAccounts } = props;

  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const answered = await readAccounts();
        if (run.live) setAccounts(answered);
      } catch (failure) {
        if (run.live)
          setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      }
    })();
    return () => {
      run.live = false;
    };
  }, [readAccounts, epoch]);

  const responsibleName = nameFromEmail(email.trim());
  const complete = name.trim() !== "" && email.includes("@") && responsibleName !== "";
  const close = (): void => {
    setOpen(false);
    setSent(null);
    setName("");
    setEmail("");
  };
  const organisations = props.partners ?? [];
  const membersOf = (organisationId: string): number =>
    accounts?.filter((one) => one.organisation.id === organisationId).length ?? 0;

  return (
    <div data-testid="comptes" className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("console.comptes.titre")}</h1>
          <p className="mt-1.5 text-sm text-gray-500">{t("console.comptes.sousTitre")}</p>
        </div>
        <button
          type="button"
          data-testid="comptes-ouvrir"
          onClick={() => {
            setOpen(true);
          }}
          className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("console.comptes.ouvrirOrganisation")}
        </button>
      </div>

      <div className="flex items-start gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-4">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
        <p className="text-sm leading-relaxed text-gray-700">{t("console.comptes.note")}</p>
      </div>

      <section>
        <h2 className="mb-3 text-sm font-semibold">{t("console.comptes.organisations")}</h2>
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          {props.partners === null ? (
            <p className="px-5 py-4 text-xs text-gray-500">{t("common.state.chargement")}</p>
          ) : organisations.length === 0 ? (
            <p className="px-5 py-4 text-sm text-gray-500">{t("console.comptes.organisationsAucune")}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {organisations.map((organisation) => {
                const members = organisation.memberCount ?? membersOf(organisation.id);
                return (
                  <li
                    key={organisation.id}
                    data-testid={`organisation-${organisation.id}`}
                    className="flex items-center gap-4 px-5 py-3.5"
                  >
                    <div className="w-64 shrink-0">
                      <p className="text-sm font-medium text-gray-900">{organisation.name}</p>
                      <p className="text-xs text-gray-500">{word(TYPE_WORDS, organisation.type)}</p>
                    </div>
                    <span data-testid="organisation-membres" className="flex-1 text-sm text-gray-600">
                      {members === 1
                        ? t("console.comptes.membreUn")
                        : fill("console.comptes.membres", { n: String(members) })}
                    </span>
                    <StatusChip status={organisation.status} />
                  </li>
                );
              })}
            </ul>
          )}
          <p className="px-5 py-3 text-sm leading-relaxed text-gray-500">
            {t("console.comptes.organisationsNote")}
          </p>
        </div>
      </section>

      <section>
        <h2 className="mb-1 text-sm font-semibold">{t("console.comptes.comptes")}</h2>
        <p data-testid="comptes-qui" className="mb-3 text-xs text-gray-500">
          {t("console.comptes.comptesQui")}
        </p>
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          {/* `DX-001` (D-1018-6): a grid from 1280 px, two columns under it; the old fixed-width flex row
              (`w-56`, `w-48`, `w-60` ...) was 1 008 px of columns that the card clipped , « Effacer » was cut
              at 1280 and the page scrolled sideways at 960. */}
          <div
            className={
              "hidden items-center gap-4 border-b border-gray-100 px-5 py-2.5 xl:grid " +
              "xl:grid-cols-[12rem_11rem_minmax(0,1fr)_9rem_8rem_14rem]"
            }
          >
            <span className="text-xs text-gray-500">{t("console.comptes.colPersonne")}</span>
            <span className="text-xs text-gray-500">{t("console.comptes.colOrganisation")}</span>
            <span className="text-xs text-gray-500">{t("console.comptes.colRole")}</span>
            <span className="text-xs text-gray-500">{t("console.comptes.colCreation")}</span>
            <span className="text-xs text-gray-500">{t("console.comptes.colStatut")}</span>
            <span className="text-xs text-gray-500" />
          </div>
          {accounts === null ? (
            <p className="px-5 py-4 text-xs text-gray-500">{refusal ?? t("common.state.chargement")}</p>
          ) : accounts.length === 0 ? (
            <p className="px-5 py-4 text-sm text-gray-500">{t("console.comptes.comptesAucun")}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {accounts.map((account) => (
                <li
                  key={account.userId}
                  data-testid={`compte-${account.userId}`}
                  className={
                    "grid grid-cols-2 items-center gap-x-4 gap-y-2 px-5 py-3.5 " +
                    "xl:grid-cols-[12rem_11rem_minmax(0,1fr)_9rem_8rem_14rem]"
                  }
                >
                  <span className="min-w-0 text-sm font-medium text-gray-900">{account.fullName}</span>
                  <span className="min-w-0 text-sm text-gray-600">{account.organisation.name}</span>
                  <span className="min-w-0 text-sm text-gray-600">{roleWord(account.role)}</span>
                  <span data-testid="compte-creation" className="min-w-0 text-xs text-gray-500">
                    {account.createdBy === "hakili"
                      ? t("console.comptes.parHakili")
                      : t("console.comptes.inviteeParLeChef")}
                  </span>
                  <span className="min-w-0">
                    <span
                      data-testid="compte-statut"
                      className={
                        "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " +
                        (STATUS_CHIP.get(account.status) ?? "bg-gray-100 text-gray-600")
                      }
                    >
                      {account.anonymised
                        ? t("console.comptes.statutEfface")
                        : word(STATUS_WORDS, account.status)}
                    </span>
                  </span>
                  <span className="col-span-2 flex flex-wrap gap-2 xl:col-span-1">
                    {account.status === "desactive" ? (
                      <GuardedButton
                        disabled
                        reason={t("console.comptes.desactiverDeja")}
                        variant="secondary"
                      >
                        {t("console.comptes.desactiver")}
                      </GuardedButton>
                    ) : props.mayDeactivate ? (
                      <button
                        type="button"
                        data-testid="compte-desactiver"
                        onClick={() => {
                          setClosed(null);
                          setClosingRefusal(null);
                          setClosing(account);
                        }}
                        className="rounded-xl border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                      >
                        {t("console.comptes.desactiver")}
                      </button>
                    ) : (
                      <GuardedButton
                        disabled
                        reason={t("console.comptes.desactiverMotif")}
                        variant="secondary"
                      >
                        {t("console.comptes.desactiver")}
                      </GuardedButton>
                    )}
                    {account.anonymised ? null : account.status !== "desactive" ? (
                      <GuardedButton disabled reason={t("console.comptes.effacerDabord")} variant="secondary">
                        {t("console.comptes.effacer")}
                      </GuardedButton>
                    ) : props.mayErasePartner ? (
                      <button
                        type="button"
                        data-testid="compte-effacer"
                        onClick={() => {
                          setClosed(null);
                          setErasing(account);
                        }}
                        className="rounded-xl border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                      >
                        {t("console.comptes.effacer")}
                      </button>
                    ) : (
                      <GuardedButton
                        disabled
                        reason={t("console.comptes.effacerSansDroit")}
                        variant="secondary"
                      >
                        {t("console.comptes.effacer")}
                      </GuardedButton>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {closed === null ? null : (
            <p
              role="status"
              data-testid="compte-desactive-fait"
              className="px-5 py-3 text-sm text-emerald-700"
            >
              {closed}
            </p>
          )}
          <p className="px-5 py-3 text-sm leading-relaxed text-gray-500">
            {t("console.comptes.comptesNote")}
          </p>
        </div>
      </section>

      <ClientAccounts
        findClients={props.findClients}
        deactivateClient={props.deactivateClient}
        reactivateClient={props.reactivateClient}
        eraseClient={props.eraseClient}
        mayErase={props.mayErase}
      />

      {erasing === null ? null : (
        <PersonErasureDialog
          fullName={erasing.fullName}
          words={{
            title: fill("console.comptes.effacerTitre", { nom: erasing.fullName }),
            warning: t("console.comptes.effacerAvertissement"),
            goes: t("console.comptes.effacerVa"),
            stays: t("console.comptes.effacerReste"),
            nameLabel: fill("console.comptes.effacerNom", { nom: erasing.fullName }),
            testPrefix: "compte-effacer",
          }}
          onClose={() => {
            setErasing(null);
          }}
          onErase={async (input) => {
            await props.erasePartnerUser(erasing.userId, input);
            setClosed(fill("console.comptes.effacerFait", { nom: erasing.fullName }));
            setErasing(null);
            setEpoch((n) => n + 1);
          }}
        />
      )}

      {closing === null ? null : (
        <Modal
          open
          title={fill("console.comptes.desactiverTitre", { nom: closing.fullName })}
          onClose={() => {
            setClosing(null);
            setClosingReason("");
          }}
          closeLabel={t("console.partenaires.fermer")}
          className="max-w-xl"
        >
          <form
            data-testid="compte-modal"
            onSubmit={(event) => {
              event.preventDefault();
              if (closingReason.trim() === "" || busy) return;
              setBusy(true);
              setClosingRefusal(null);
              void (async () => {
                try {
                  await props.deactivateAccount(closing.userId, closingReason.trim());
                  setClosed(fill("console.comptes.desactiverFait", { nom: closing.fullName }));
                  setClosing(null);
                  setClosingReason("");
                  setEpoch((n) => n + 1);
                } catch (failure) {
                  setClosingRefusal(
                    (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
                  );
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            <p className="mb-4 text-sm leading-relaxed text-gray-600">
              {t("console.comptes.desactiverPhrase")}
            </p>
            <label className="block text-xs font-medium text-gray-600">
              {t("console.comptes.clientMotif")}
              <textarea
                data-testid="compte-motif-saisie"
                value={closingReason}
                rows={3}
                onChange={(event) => {
                  setClosingReason(event.target.value);
                }}
                className="mt-1.5 block w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-indigo-400"
              />
            </label>
            {closingRefusal === null ? null : (
              <p role="alert" data-testid="compte-refus" className="mt-3 text-sm text-rose-700">
                {closingRefusal}
              </p>
            )}
            <div className="mt-5 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setClosing(null);
                  setClosingReason("");
                }}
                className="text-sm font-medium text-gray-600 hover:text-gray-700"
              >
                {t("console.comptes.clientAnnuler")}
              </button>
              <button
                type="submit"
                data-testid="compte-confirmer"
                disabled={closingReason.trim() === "" || busy}
                className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40"
              >
                {t("console.comptes.clientDesactiver")}
              </button>
            </div>
          </form>
        </Modal>
      )}

      <PartnerAddModal open={open} title={t("console.comptes.ouvrirOrganisation")} onClose={close}>
        {sent !== null ? (
          <div data-testid="organisation-ouverte" className="py-4 text-center">
            <p className="text-base font-semibold">{t("console.partenaires.invitationEnvoyee")}</p>
            <p className="mt-2 text-sm leading-relaxed text-gray-500">
              {fill("console.comptes.organisationOuverte", { nom: sent })}
            </p>
            <button
              type="button"
              onClick={close}
              className="mt-5 w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
            >
              {t("console.partenaires.fermer")}
            </button>
          </div>
        ) : (
          <form
            data-testid="organisation-formulaire"
            onSubmit={(event) => {
              event.preventDefault();
              if (!complete) return;
              void (async () => {
                setBusy(true);
                setRefusal(null);
                try {
                  await props.openOrganisation({
                    name: name.trim(),
                    type,
                    responsibleName,
                    responsibleEmail: email.trim(),
                  });
                  setSent(name.trim());
                  setEpoch((now) => now + 1);
                  props.onOpened();
                } catch (failure) {
                  setRefusal(
                    (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
                  );
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            <p className="mb-4 text-xs text-gray-500">{t("console.comptes.ouvrirSous")}</p>
            <label className="mb-3 block text-xs font-medium text-gray-600">
              {t("console.comptes.typeOrganisation")}
              <select
                data-testid="organisation-type"
                value={type}
                onChange={(event) => {
                  setType(event.target.value);
                }}
                className="mt-1.5 block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-400"
              >
                {OPENABLE_TYPES.map((one) => (
                  <option key={one} value={one}>
                    {word(TYPE_WORDS, one)}
                  </option>
                ))}
              </select>
            </label>
            <label className="mb-3 block text-xs font-medium text-gray-600">
              {t("console.comptes.nomOrganisation")}
              <input
                data-testid="organisation-nom"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
                placeholder="AFG Bank Mali"
                className="mt-1.5 block w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-indigo-400"
              />
            </label>
            <p className="mb-3 text-sm leading-relaxed text-gray-500">{t("console.comptes.domaineEnMots")}</p>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              {t("console.comptes.premierResponsable")}
              <div className="mt-1.5 flex items-center gap-2.5 rounded-xl border border-gray-200 px-3 py-2.5">
                <Mail className="h-4 w-4 text-gray-500" aria-hidden="true" />
                <input
                  data-testid="organisation-email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                  placeholder="prenom.nom@afgbank.ml"
                  className="min-w-0 flex-1 text-sm outline-none"
                />
              </div>
            </label>
            <p className="mb-5 text-xs text-gray-500">
              {responsibleName === "" ? t("console.partenaires.nomDeduit") : responsibleName}
            </p>
            {refusal === null ? null : (
              <p role="alert" data-testid="organisation-refus" className="mb-3 text-sm text-rose-600">
                {refusal}
              </p>
            )}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={close}
                className="text-sm font-medium text-gray-600 hover:text-gray-700"
              >
                {t("console.equipe.annuler")}
              </button>
              <button
                type="submit"
                data-testid="organisation-creer"
                disabled={!complete || busy}
                className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                {t("console.partenaires.creerEtInviter")}
              </button>
            </div>
          </form>
        )}
      </PartnerAddModal>
    </div>
  );
}
